import * as React from 'react'

import { create } from '@bufbuild/protobuf'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Field, Flex, Stack, Text } from '@chakra-ui/react'

import {
  Input,
  Label,
  PrimaryButton,
  RadioButton,
  Term,
} from '@enechain/ecloud-designsystem'
import { FaTriangleExclamationRegular } from '@enechain/ecloud-icons'

import CancelButton from '~/components/parts/CancelButton'
import { ProfilePicture } from '~/components/parts/ProfilePicture'
import {
  Language,
  LicenseSchema,
  Member,
  MemberRole,
  UserRoleSettingSchema,
} from '~/gen/proto/bff/v1/model_pb'
import {
  MemberEditSchema,
  memberEditSchema,
} from '~/pages/Members/components/MemberEdit/memberEditSchema'
import { NEW_MEMBER_KEY } from '~/pages/Members/create'
import { useSelectableUserRoles } from '~/pages/Members/hooks/useSelectableUserRoles'
import { useMemberMutationState } from '~/pages/Members/state/memberMutation'
import { isUserRoleConfigurable } from '~/pages/Members/utils/license'
import { validateEmptyNumber } from '~/validation/phonenumber'

import { UserRoleSelect } from './UserRoleSelect'

type Props = {
  member: Member
  onNext: () => void
  onCancel: () => void
}

const MemberEditView: React.FC<Props> = ({ member, onNext, onCancel }) => {
  const { t } = useTranslation()
  const { edited: memberEdited } = useMemberMutationState(member.id)
  const userRoleSetting = useSelectableUserRoles()
  const isCreate = member.id === NEW_MEMBER_KEY

  const schema = memberEditSchema(t)
  const formMethods = useForm<MemberEditSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: member.id,
      role:
        member.role === MemberRole.UNKNOWN ? MemberRole.MEMBER : member.role,
      language:
        member.language === Language.UNKNOWN
          ? Language.JAPANESE
          : member.language,
    },
  })
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setError,
  } = formMethods
  const onSubmit = (data: MemberEditSchema): void => {
    if (
      !validateEmptyNumber(
        {
          phoneNumber: { old: member.phoneNumber, newVal: data.phoneNumber },
          mobileNumber: { old: member.mobileNumber, newVal: data.mobileNumber },
        },
        setError,
      )
    ) {
      return
    }
    memberEdited(data)
    void onNext()
  }

  return (
    <Stack gap="6" width="100%">
      {!isCreate && (
        <Flex alignItems="center" gap="6">
          {member.imageUrl && <ProfilePicture src={member.imageUrl} />}
          <Flex flexDirection="column">
            <Text textStyle="headerMd">{member.name}</Text>
            <Text>{member.email}</Text>
          </Flex>
        </Flex>
      )}
      <Text textStyle="headerMd">{t('pages.members.info')}</Text>
      <Flex
        flexDirection="column"
        gap="4"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="radiiMd"
        paddingX="4"
        paddingY="6"
      >
        <Term>
          <Label required>{t('pages.members.label.name')}</Label>
          <Input
            defaultValue={member.name}
            {...register('name')}
            width="50%"
            error={errors.name?.message}
          />
          <Text textStyle="textSm" color="Text.midEmphasis">
            {t('pages.members.description.name')}
          </Text>
        </Term>
        <Term>
          <Label>{t('pages.members.label.englishName')}</Label>
          <Input
            defaultValue={member.englishName}
            {...register('englishName')}
            width="50%"
            error={errors.englishName?.message}
          />
          <Text textStyle="textSm" color="Text.midEmphasis">
            {t('pages.members.description.englishName')}
          </Text>
        </Term>
        <Term>
          <Label required>{t('pages.members.label.email')}</Label>
          <Input
            defaultValue={member.email}
            {...register('email')}
            width="50%"
            error={errors.email?.message}
          />
          <Flex
            textStyle="textMd"
            alignItems="center"
            gap="2"
            padding="4"
            color="Text.warningMid"
            fontWeight="bold"
            backgroundColor="Surface.warningLow"
          >
            <FaTriangleExclamationRegular
              boxSize="24px"
              color="Object.warningHigh"
            />
            {!isCreate ? (
              <Stack>
                <Text>{t('pages.members.description.forbidGroupAddress')}</Text>
                <Text textStyle="textMd">
                  {t('pages.members.description.noticeSendVerification')}
                </Text>
              </Stack>
            ) : (
              <Text>{t('pages.members.description.forbidGroupAddress')}</Text>
            )}
          </Flex>
        </Term>
        <Field.Root>
          <Controller
            control={control}
            name="role"
            render={({ field: { onChange, value } }): React.ReactElement => (
              <Term>
                <Label required>{t('pages.members.label.role')}</Label>
                <RadioButton
                  defaultValue={member.role.toString()}
                  onValueChange={({ value }): void => {
                    if (value !== null) {
                      onChange(value)
                    }
                  }}
                  radios={[
                    {
                      label: t('pages.members.value.admin'),
                      value: MemberRole.ADMIN.toString(),
                    },
                    {
                      label: t('pages.members.value.member'),
                      value: MemberRole.MEMBER.toString(),
                    },
                  ]}
                  value={value.toString()}
                />
              </Term>
            )}
          />
        </Field.Root>

        <Field.Root>
          <Controller
            control={control}
            name="language"
            render={({ field: { onChange, value } }): React.ReactElement => (
              <Term>
                <Label required>{t('pages.members.label.language')}</Label>
                <RadioButton
                  defaultValue={member.language.toString()}
                  onValueChange={({ value }): void => {
                    if (value !== null) {
                      onChange(value)
                    }
                  }}
                  radios={[
                    {
                      label: t('pages.members.value.japanese'),
                      value: Language.JAPANESE.toString(),
                    },
                    {
                      label: t('pages.members.value.english'),
                      value: Language.ENGLISH.toString(),
                    },
                  ]}
                  value={value.toString()}
                />
              </Term>
            )}
          />
        </Field.Root>

        <Term>
          <Label>{t('pages.members.label.phoneNumber')}</Label>
          <Input
            defaultValue={member.phoneNumber}
            {...register('phoneNumber')}
            width="50%"
            error={errors.phoneNumber?.message}
          />
        </Term>

        <Term>
          <Label>{t('pages.members.label.mobileNumber')}</Label>
          <Input
            defaultValue={member.mobileNumber}
            {...register('mobileNumber')}
            width="50%"
            error={errors.mobileNumber?.message}
          />
        </Term>
      </Flex>

      <Flex
        flexDirection="column"
        gap="2"
        display={
          isUserRoleConfigurable(
            userRoleSetting.map((v) => v.license ?? create(LicenseSchema)),
          )
            ? 'flex'
            : 'none'
        }
      >
        <Text textStyle="headerMd">{t('pages.members.roleSettings')}</Text>

        <Flex
          flexDirection="column"
          gap="4"
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="radiiMd"
          paddingX="4"
          paddingY="6"
        >
          {userRoleSetting.map((role, i) => {
            const { userRoleSetting, license } = role
            if (!license) {
              return null
            }
            const settings = userRoleSetting.map((setting) => {
              const isSetting = member.userRoleCodes.some(
                (code) => setting.userRoleCode === code,
              )
              return create(UserRoleSettingSchema, {
                ...setting,
                isSetting: isSetting,
              })
            })
            return (
              <UserRoleSelect
                key={license.planCode}
                formMethods={formMethods}
                index={i}
                license={license}
                userRoleSetting={settings}
              />
            )
          })}
        </Flex>
      </Flex>
      <Flex justifyContent="right" gap="2" width="100%">
        <CancelButton onClick={onCancel} />
        <PrimaryButton onClick={handleSubmit(onSubmit)}>
          {t('action.next')}
        </PrimaryButton>
      </Flex>
    </Stack>
  )
}

export default React.memo(MemberEditView)
